import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight01Icon, PlayIcon, Refresh01Icon } from "@hugeicons/core-free-icons";
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, RiskBadge, StatusBadge, PriorityBadge, EmptyState } from '@/components/common/Bits';
import { fmtDate } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

export default function Reevaluations() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const load = () => api.get('/reevaluations').then(({ data }) => setRows(data));
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/reevaluations/${id}`, { status });
      toast.success(`Reevaluación ${status.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Skeleton className="h-96 rounded-xl" />;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <PageHeader
        title="Cola de reevaluación"
        subtitle="Cambios de modelo, proveedor, volumen, datos o normativa que obligan a una nueva versión de la evaluación. El riesgo anterior queda conservado."
        testId="reevals-header"
      />
      {rows.length === 0 ? (
        <EmptyState icon={Refresh01Icon} title="Sin reevaluaciones activas" />
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-card overflow-x-auto">
          <Table data-testid="reevals-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {[
                  'Iniciativa',
                  'Motivo',
                  'Riesgo anterior → estimado',
                  'Responsable',
                  'Fecha límite',
                  'Prioridad',
                  'Estado',
                  'Acciones',
                ].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`reeval-row-${r.id}`}>
                  <TableCell className="min-w-52">
                    <Link
                      to={`/iniciativas/${r.initiative_id}`}
                      className="font-medium text-sm hover:text-primary transition-colors">
                      {r.initiative_name}
                    </Link>
                    <p className="text-[11px] text-muted-foreground">Fuente: {r.source}</p>
                  </TableCell>
                  <TableCell className="min-w-56">
                    <p className="text-xs font-medium">{r.trigger}</p>
                    <p className="text-[11px] text-muted-foreground line-clamp-2">{r.reason}</p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <RiskBadge level={r.previous_risk} /> → <RiskBadge level={r.estimated_new_risk} />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{r.owner}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDate(r.due_date)}
                  </TableCell>
                  <TableCell>
                    <PriorityBadge priority={r.priority} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {can.edit && r.status === 'Pendiente' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-border"
                        onClick={() => setStatus(r.id, 'En curso')}
                        data-testid={`start-reeval-${r.id}`}>
                        <HugeiconsIcon icon={PlayIcon} className="h-3 w-3 mr-1" /> Iniciar
                      </Button>
                    )}
                    {can.edit && r.status === 'En curso' && (
                      <Button
                        asChild
                        size="sm"
                        className="h-7 text-xs bg-accent hover:bg-accent/90 text-accent-foreground"
                        data-testid={`run-reeval-${r.id}`}>
                        <Link to={`/iniciativas/${r.initiative_id}/evaluar?reeval=${r.id}`}>
                          Ejecutar evaluación <HugeiconsIcon icon={ArrowRight01Icon} className="h-3 w-3 ml-1" />
                        </Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
